<script setup lang="ts">
  import { LPolygon, LTooltip } from '@vue-leaflet/vue-leaflet'
  import type { SectionSelectionPolygon } from '@/stores/type/selectionPolygon.type'
  import type { LatLngExpression } from 'leaflet'
  import { findGIPointsToRemove } from '@/utils/map'

  const props = defineProps({
    item: {
      type: Object as PropType<SectionSelectionPolygon>,
      required: true
    }
  })
  const store = useProjectStore()

  const selectionPolygonsStore = useSelectionPolygonsStores()
  const sectionLinesGIPointsStore = useSectionLinesGIPointsStore()
  const authorizationStore = useAuthorizationStore()
  const polygon = ref()
  const { selectedItems, polygonsBySectionLineId } = storeToRefs(selectionPolygonsStore)
  const { profileDrawer } = storeToRefs(store)

  const reversedCoordinates = computed(() =>
    props.item.geometry.coordinates[0].map((coordinate: LatLngExpression[]) => [
      coordinate[1],
      coordinate[0]
    ])
  )

  const isSelected = computed(() => {
    return selectedItems.value.map((item) => item.id).includes(props.item.id)
      ? { weight: 5, color: 'black' }
      : { weight: 2, color: 'grey' }
  })

  function onPolygonReady() {
    polygon.value.leafletObject.on('click', () => {
      selectionPolygonsStore.addOrRemoveSelectedPolygon(props.item)
    })
    polygon.value.leafletObject.bringToBack()
  }

  const removePolygons = async () => {
    const giPointsStore = useGIPointsStore()
    const allPolygons = polygonsBySectionLineId.value(props.item.properties.section_line)
    for (const polygonToRemove of selectedItems.value) {
      const giPointsToRemove = findGIPointsToRemove(
        polygonToRemove,
        allPolygons,
        giPointsStore.items
      )

      await sectionLinesGIPointsStore.removeGIPointsFromSectionLine(
        giPointsToRemove,
        polygonToRemove.properties.section_line
      )
    }

    await selectionPolygonsStore.removeSelectedPolygons()
  }

  const canChangePolygon = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: 'change_sectionselectionpolygon'
    })
  })

  const canDeletePolygon = computed(() => {
    return (
      authorizationStore.hasServicePermission({
        authSource: 'ProjectUser',
        allowed: 'delete_sectionselectionpolygon'
      }) && selectedItems.value.length > 0
    )
  })

  const polygonOptions = computed(() => ({
    zIndex: 1,
    contextmenu: store.profileDrawer.shown,
    contextmenuInheritItems: false,
    contextmenuItems: [
      {
        text: 'Delete Polygons',
        callback: removePolygons,
        index: 0,
        disabled: !canDeletePolygon.value
      },
      {
        text: 'Edit Name',
        callback: () => {
          selectionPolygonsStore.toggleUpdatePolygonDialog(props.item)
        },
        index: 1,
        disabled: !canChangePolygon.value
      }
    ]
  }))

  // This is a workaround to make sure that the context menu can be updated with certain variables
  watch(polygonOptions, (options) => {
    const polygonLeafletObject = polygon.value.leafletObject
    if (!polygonLeafletObject) {
      return
    }
    polygonLeafletObject.unbindContextMenu()
    if (profileDrawer.value.shown) {
      polygonLeafletObject.bindContextMenu(options)
    }
  })

  // This is a workaround to make sure that the context menu can be updated with certain variables
  watch(profileDrawer.value, (options) => {
    const polygonLeafletObject = polygon.value.leafletObject
    if (!polygonLeafletObject) {
      return
    }
    if (options.shown) {
      polygonLeafletObject.bindContextMenu(options)
    } else {
      polygonLeafletObject.unbindContextMenu()
    }
  })
</script>
<template>
  <l-polygon
    ref="polygon"
    @ready="onPolygonReady"
    :options="polygonOptions"
    :lat-lngs="reversedCoordinates"
    :fill-opacity="0.1"
    :fill-color="isSelected.color"
    :weight="isSelected.weight"
    :color="isSelected.color"
  >
    <l-tooltip> {{ props.item.properties.zone_name }}</l-tooltip>
  </l-polygon>
</template>

<style scoped></style>
